import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {Building2, Users, Briefcase, AlertTriangle, BookOpen, TrendingUp, Settings, ChevronRight} from 'lucide-react'
import AdminLayout from '@/components/AdminLayout'
import { lumi } from '@/lib/lumi'

interface GlobalStats {
  empresas: number
  colaboradores: number
  vacantes: number
  casosActivos: number
}

const STAT_STYLES = [
  { color: 'text-blue-600', bg: 'bg-blue-50' },
  { color: 'text-emerald-600', bg: 'bg-emerald-50' },
  { color: 'text-violet-600', bg: 'bg-violet-50' },
  { color: 'text-amber-600', bg: 'bg-amber-50' },
]

function AdminDashboard() {
  const navigate = useNavigate()
  const [stats, setStats] = useState<GlobalStats>({ empresas: 0, colaboradores: 0, vacantes: 0, casosActivos: 0 })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const isAdmin = localStorage.getItem('beeznz_admin')
    if (!isAdmin) {
      navigate('/login')
      return
    }
    const fetchStats = async () => {
      try {
        const [empRes, colRes, vacRes, casosRes] = await Promise.all([
          lumi.entities.companies.list({ limit: 1 }),
          lumi.entities.collaborators.list({ limit: 1 }),
          lumi.entities.vacancies.list({ filter: { status: 'publicada' }, limit: 1 }),
          lumi.entities.cases.list({ filter: { status: 'en_revision' }, limit: 1 }),
        ])
        setStats({
          empresas: empRes.total ?? empRes.list.length,
          colaboradores: colRes.total ?? colRes.list.length,
          vacantes: vacRes.total ?? vacRes.list.length,
          casosActivos: casosRes.total ?? casosRes.list.length,
        })
      } catch (e) {
        console.error('Error al cargar stats admin:', e)
      } finally {
        setLoading(false)
      }
    }
    fetchStats()
  }, [])

  const statCards = [
    { label: 'Empresas registradas', value: stats.empresas, icon: Building2, ...STAT_STYLES[0] },
    { label: 'Colaboradores activos', value: stats.colaboradores, icon: Users, ...STAT_STYLES[1] },
    { label: 'Vacantes activas', value: stats.vacantes, icon: Briefcase, ...STAT_STYLES[2] },
    { label: 'Casos en revisión', value: stats.casosActivos, icon: AlertTriangle, ...STAT_STYLES[3] },
  ]

  return (
    <AdminLayout title="Dashboard global" subtitle="Resumen general de la plataforma Beeznz Work">
      {/* Stats */}
      <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {statCards.map(({ label, value, icon: Icon, color, bg }) => (
          <div
            key={label}
            className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
          >
            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${bg}`}>
              <Icon size={20} className={color} />
            </div>
            <p className="mt-3 text-2xl font-bold text-gray-900">{loading ? '—' : value}</p>
            <p className="mt-0.5 text-sm text-gray-500">{label}</p>
          </div>
        ))}
      </div>

      {/* Gestión del sistema */}
      <div className="mb-6">
        <h2 className="mb-4 text-base font-semibold text-gray-900">Gestión del sistema</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { label: 'Ver todas las empresas', desc: 'Administra perfiles corporativos', to: '/admin/empresas', icon: Building2 },
            { label: 'Ver todos los colaboradores', desc: 'Gestiona perfiles de usuarios', to: '/admin/colaboradores', icon: Users },
            { label: 'Revisar casos activos', desc: 'Aprueba o rechaza reportes laborales', to: '/admin/casos', icon: AlertTriangle },
            { label: 'Publicar capacitación', desc: 'Cursos y talleres para colaboradores', to: '/admin/capacitaciones', icon: BookOpen },
            { label: 'Indicadores globales', desc: 'Métricas y tendencias de la plataforma', to: '/admin/reportes', icon: TrendingUp },
            { label: 'Configuración', desc: 'Categorías, permisos y sistema', to: '/admin/configuracion', icon: Settings },
          ].map(({ label, desc, to, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className="group flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition hover:border-amber-300 hover:shadow"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-50">
                <Icon size={18} className="text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">{label}</p>
                <p className="text-xs text-gray-500 truncate">{desc}</p>
              </div>
              <ChevronRight size={15} className="shrink-0 text-gray-300 group-hover:text-gray-500" />
            </Link>
          ))}
        </div>
      </div>
    </AdminLayout>
  )
}

export default AdminDashboard
